import React, { useCallback, useState } from "react";
import { UiLanguageProvider } from "../ui/uiText";
import type { GermanAddress } from "./formalGermanTranslations";
import type { Lang, LocalizedText, TranslationMaps } from "./i18n";
import { hasTranslationKey, normalizeLang, pickLang, textFor, translateKey } from "./i18n";

function getInitialStoredLang(): Lang {
  const s = typeof localStorage !== "undefined" ? localStorage.getItem("language") : null;
  const stored = normalizeLang(s);
  if (stored) return stored;
  const nav = typeof navigator !== "undefined" ? navigator.language : "";
  return pickLang({ browserLanguage: nav });
}

const StoredLanguageContext = React.createContext<[Lang, (lang: Lang) => void] | null>(null);

export function StoredLanguageProvider(props: { children: React.ReactNode }) {
  const [stored, setStored] = useState<Lang>(getInitialStoredLang);
  const setter = useCallback((lang: Lang) => {
    if (typeof localStorage !== "undefined") localStorage.setItem("language", lang);
    setStored(lang);
  }, []);
  return (
    <StoredLanguageContext.Provider value={[stored, setter]}>
      {props.children}
    </StoredLanguageContext.Provider>
  );
}

export function useStoredLanguage(): [Lang, (lang: Lang) => void] {
  const ctx = React.useContext(StoredLanguageContext);
  if (!ctx) throw new Error("useStoredLanguage must be used within StoredLanguageProvider.");
  return ctx;
}

export type I18nContextValue = {
  lang: Lang;
  germanAddress?: GermanAddress;
  text: (value: LocalizedText) => string;
  tr: (key: string) => string;
};

const I18nContext = React.createContext<I18nContextValue | null>(null);

export function I18nProvider(props: {
  children: React.ReactNode;
  lang?: Lang;
  /** Produktspezifische Übersetzungs-Dictionaries (de + en). */
  translations: TranslationMaps;
  /** German address used by Shared-owned labels; other languages stay unchanged. */
  germanAddress?: GermanAddress;
}) {
  const [storedLang] = useStoredLanguage();
  const { translations } = props;

  const computedLang = React.useMemo(() => {
    const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
    return pickLang({
      storedLanguage: storedLang,
      browserLanguage
    });
  }, [storedLang]);

  const lang = props.lang || computedLang;

  const value = React.useMemo<I18nContextValue>(() => {
    const missing = new Set<string>();
    const warnMissing = (key: string) => {
      if (!hasTranslationKey(translations, key) && typeof console !== "undefined" && !missing.has(key)) {
        missing.add(key);
        console.warn(`[i18n] Missing translation for: "${key}"`);
      }
    };
    return {
      lang,
      germanAddress: props.germanAddress,
      text: (v) => {
        if (typeof v === "string") warnMissing(v);
        return textFor(translations, lang, v);
      },
      tr: (key: string) => {
        const translated = translateKey(translations, lang, key);
        if (translated !== key) return translated;
        warnMissing(key);
        return key;
      }
    };
  }, [lang, translations, props.germanAddress]);

  return (
    <I18nContext.Provider value={value}>
      <UiLanguageProvider value={lang}>{props.children}</UiLanguageProvider>
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const ctx = React.useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider.");
  return ctx;
}

/** Für Shared-Bausteine, die auch in Apps ohne den Shared-`I18nProvider` laufen. */
export function useI18nOptional(): I18nContextValue | null {
  return React.useContext(I18nContext);
}
